/**
 * Blueprint actions — one session lifecycle, parameterised by blueprint.
 *
 * Every blueprint app creates the same canonical session the Session app does;
 * the blueprint only decides the method, the pacing, and what the parts are
 * called. Nothing here is app-specific, which is the point: the tail costs a
 * record, not a codebase.
 */

import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { uniqueLines } from '@/lib/uniqueLines';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { sessionHasObviousOrLockedCriteria } from '@/lib/sessionToolCatalog';
import type { PrioritizationSession } from '@/types/prioritization/session';
import { createSessionWithFeedback, type CreateSessionFlowResult, type CreateSessionFlowOptions } from '@/lib/sessions/createSessionFlow';
import type { AppBlueprint } from './types';

/** Marks a session as belonging to a blueprint app, so its list can find it. */
const BLUEPRINT_CONFIG_KEY = 'blueprint_app_id';

/**
 * True when this blueprint's method scores against criteria the owner has to
 * name — weighted scoring, yes; dot voting or impact/effort, no.
 *
 * Asked of the same check that decides whether "Open" may go ahead
 * (`sessionHasObviousOrLockedCriteria`, read by the session store before
 * DRAFT → CRITERIA_FINALIZED), for a new blueprint session: a draft with no
 * criteria and no track. So setup asks for criteria exactly when Open would
 * otherwise refuse with "At least one criterion must be selected".
 */
export function blueprintNeedsCriteria(blueprint: AppBlueprint): boolean {
  return !sessionHasObviousOrLockedCriteria({
    selectedToolId: blueprint.toolId,
    criteriaConfig: { criteriaIds: [], weights: {}, reconciliationRequiredSet: [] },
    trackId: null,
    state: 'DRAFT',
  });
}

/**
 * The owner's criteria as the session will carry them: trimmed, inner spaces
 * collapsed, blanks dropped, and repeats (ignoring case) kept once — two lines
 * with the same words would be one criterion scored twice.
 */
export function normalizeBlueprintCriteria(lines: readonly string[]): string[] {
  return uniqueLines(lines);
}

/** One criterion per line, as typed on the setup screen. */
export function blueprintCriteriaFromText(text: string): string[] {
  return normalizeBlueprintCriteria(text.split('\n'));
}

/**
 * Why this setup cannot start yet on account of its criteria, in plain words —
 * or null when it can. Null for a blueprint whose method needs none.
 */
export function blueprintCriteriaProblem(
  blueprint: AppBlueprint,
  criteria: readonly string[],
): string | null {
  if (!blueprintNeedsCriteria(blueprint)) return null;
  if (normalizeBlueprintCriteria(criteria).length > 0) return null;
  return `Add at least one line. Each ${blueprint.copy.candidateNoun} is scored on these.`;
}

export interface CreateBlueprintSessionInput {
  blueprint: AppBlueprint;
  /** The decision, in the owner's words. */
  name: string;
  /** The things being weighed. */
  candidates: string[];
  /**
   * What matters, in the owner's words. Required (at least one) when the
   * blueprint's method scores against criteria; ignored otherwise.
   */
  criteria?: string[];
  facilitatorId: string;
  /**
   * An offline blueprint's answer window, in days from now (roadmap S-9,
   * ruling R14). Ignored for a blueprint run live in the room.
   */
  windowDays?: number;
  /** Shows the non-blocking "Created — note: …" toast for a warn-and-proceed gate. */
  toast?: CreateSessionFlowOptions['toast'];
}

/** The answer windows an offline blueprint offers, in days; the first is the default. */
export const BLUEPRINT_WINDOW_DAYS = [3, 1, 7] as const;

/**
 * An offline blueprint's window (S-9, R14): it opens now and closes `days`
 * later, at the end of that day's working hours (17:00 local), so "3 days"
 * means people have three working days. Live blueprints have no window.
 */
export function blueprintWindow(
  blueprint: Pick<AppBlueprint, 'timing'>,
  days: number | undefined,
  now: Date = new Date(),
): { opensAt: Date; closesAt: Date } | null {
  if (blueprint.timing !== 'async') return null;
  const span = days && days > 0 ? days : BLUEPRINT_WINDOW_DAYS[0];
  const closesAt = new Date(now);
  closesAt.setDate(closesAt.getDate() + span);
  closesAt.setHours(17, 0, 0, 0);
  return { opensAt: now, closesAt };
}

export async function createBlueprintSession(
  input: CreateBlueprintSessionInput,
): Promise<CreateSessionFlowResult> {
  // Refuse here rather than create a draft that can never be opened.
  const criteriaProblem = blueprintCriteriaProblem(input.blueprint, input.criteria ?? []);
  if (criteriaProblem) return { ok: false, error: criteriaProblem };

  // The criteria ride `initialCriteriaIds` into `criteria_config.criteriaIds` —
  // the one field the Open check reads and the scoring tool scores against
  // (`resolveSessionScoringCriteria`). They are the owner's own words, not
  // workspace attributes: an isolated session writes nothing back to the
  // workspace, so a quick hire or vendor pick must not add to its attribute
  // list. Weights start equal (the store's default for initial criteria).
  const criteria = blueprintNeedsCriteria(input.blueprint)
    ? normalizeBlueprintCriteria(input.criteria ?? [])
    : [];

  return createSessionWithFeedback({
    name: input.name.trim(),
    facilitatorId: input.facilitatorId,
    createdBy: input.facilitatorId,
    intent: 'rank_priorities',
    sessionContextMode: 'isolated',
    isolatedItems: input.candidates,
    selectedToolId: input.blueprint.toolId,
    ...(criteria.length > 0 && { initialCriteriaIds: criteria }),
    // The blueprint id rides in toolConfig, not a new column: which app framed
    // this run is presentation, not a governed fact (Doc 31 first law).
    toolConfig: { [BLUEPRINT_CONFIG_KEY]: input.blueprint.appId },
    // A live blueprint runs in the room ('live'); an offline one runs in a
    // window that closes answers (S-9, R14 and R2).
    modeConfig: {
      timingMode: input.blueprint.timing === 'sync' ? 'live' : 'async',
      ...(blueprintWindow(input.blueprint, input.windowDays) ?? {}),
    },
    workspaceId: getCurrentWorkspaceId() ?? undefined,
  }, { toast: input.toast });
}

/** True when a session was created by this blueprint app. */
export function isBlueprintSession(
  session: PrioritizationSession,
  appId: string,
): boolean {
  const config = (session.toolConfig ?? {}) as Record<string, unknown>;
  return config[BLUEPRINT_CONFIG_KEY] === appId;
}

/** The candidates in a blueprint session, resolved to titles for display. */
export function blueprintCandidates(
  session: PrioritizationSession,
): Array<{ id: string; name: string }> {
  if (session.sessionContextMode === 'isolated') {
    return uniqueLines(session.isolatedItems ?? []).map((text) => ({ id: text, name: text }));
  }
  const { getItemById } = useItemsStore.getState();
  return (session.itemIds ?? []).map((id) => ({
    id,
    name: getItemById(id)?.title ?? id,
  }));
}

/**
 * Split the ranked result into what the group chose and what it did not.
 *
 * `topFraction: 1` means the whole ranking is the answer, so `rest` is empty
 * and the renderer hides the second group.
 */
export function blueprintResult(
  session: PrioritizationSession,
  blueprint: AppBlueprint,
): { top: Array<{ id: string; name: string }>; rest: Array<{ id: string; name: string }> } {
  const ranked = session.rankingOutput?.rankedItemIds ?? [];
  if (ranked.length === 0) return { top: [], rest: [] };

  const byId = new Map(blueprintCandidates(session).map((c) => [c.id, c.name]));
  const inOrder = ranked.map((id: string) => ({ id, name: byId.get(id) ?? id }));

  if (blueprint.topFraction >= 1) return { top: inOrder, rest: [] };
  const cut = Math.max(1, Math.ceil(inOrder.length * blueprint.topFraction));
  return { top: inOrder.slice(0, cut), rest: inOrder.slice(cut) };
}

// ---------------------------------------------------------------------------
// Owners on the result (roadmap S-10, ruling R15): picked from the workspace's
// members and written as canonical commitments. A commitment carries no link
// to the session, so its description names the session and the line; the run
// screen reads the owners back from the commitments with these two.
// ---------------------------------------------------------------------------

/** The key for an owner of the whole result rather than of one line. */
export const RESULT_OWNER_KEY = '__result__';

/** The description an owner's commitment carries: where it came from, in words, then the reference. */
export function ownerCommitmentDescription(
  session: Pick<PrioritizationSession, 'id' | 'name'>,
  appName: string,
  lineId?: string,
): string {
  const where = `Owned out of “${session.name}” (${appName}).`;
  return lineId ? `${where} Session ${session.id}, line ${lineId}.` : `${where} Session ${session.id}, the result.`;
}

/** Who owns what on this session's result, read back from the commitments. */
export function ownersFromCommitments(
  commitments: ReadonlyArray<{ description: string | null; ownerName: string | null; ownerId: string | null }>,
  sessionId: string,
): Record<string, string> {
  const marker = `Session ${sessionId}, `;
  const out: Record<string, string> = {};
  for (const c of commitments) {
    const at = c.description?.indexOf(marker) ?? -1;
    if (at < 0 || !c.description) continue;
    const rest = c.description.slice(at + marker.length).replace(/\.$/, '');
    const key = rest === 'the result' ? RESULT_OWNER_KEY : rest.startsWith('line ') ? rest.slice(5) : null;
    if (key && !out[key]) out[key] = c.ownerName ?? 'Someone';
  }
  return out;
}
